import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import MagazineStageSimple from '../../Components/MagazineStageSimple/MagazineStageSimple';
import imgCapa from '../../assets/news-events/compatible_hydraulic_hose/Capa.png';
import imgImagem1 from '../../assets/news-events/compatible_hydraulic_hose/Imagem 1.png';
import imgImagem2 from '../../assets/news-events/compatible_hydraulic_hose/Imagem 2.png';
import imgImagem3 from '../../assets/news-events/compatible_hydraulic_hose/Imagem 3.png';
import imgImagem4 from '../../assets/news-events/compatible_hydraulic_hose/Imagem 4.png';
import imgImagem5 from '../../assets/news-events/compatible_hydraulic_hose/Imagem 5.png';
import imgImagem6 from '../../assets/news-events/compatible_hydraulic_hose/Imagem 6.png';
import MagazineTextBlock from '../../Components/MagazineTextBlock/MagazineTextBlock';

const CompatibleHydraulicHose = () => {
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top on component mount
    }, []);

    const { t } = useTranslation();
    const ogTitle = t('Compatible hydraulic hose: how to choose');
    const ogDescription = t('Learn which information truly identifies a compatible hydraulic hose: pressure, inner diameter, fluid, temperature, fittings, and the real movements of the machine. Specify the right assembled hose with HANSA-FLEX.');
    const ogImage = `https://www.hansa-flex.com.br/${imgCapa}`;
    const ogUrl = `https://www.hansa-flex.com.br/compatible-hydraulic-hose`;

    return (
        <div>
            <Helmet>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:type" content="News" />
            </Helmet>

            <MagazineStageSimple
                imageSrc={imgCapa}
                imageAlt="Compatible hydraulic hose: how to choose"
                // meta="June 24, 2026 | Gabriel Nascimento"
                title={"Compatible hydraulic hose: how to choose"}
                text={"Two machines of the same model can use different hoses due to the year of manufacture, the hydraulic configuration, the installed implements, or changes made during previous maintenance. For this reason, simply copying the length or taking a photo to the supplier can result in a part that appears to fit but does not operate safely. In this article, you will see which information truly identifies a compatible hydraulic hose and how to gather it before replacement."}
                readingTimeMinutes={8}
            />

            <MagazineTextBlock
                imageSrc={imgImagem1}
                imageAlt="The machine model is only the starting point"
                title="The machine model is only the starting point"
                paragraphs={[
                    "When a hose ruptures, the pressure to get the equipment back into operation favors quick decisions. The risk lies in choosing the replacement based solely on the machine's brand and model.",
                    "This data helps locate the application, but it must be combined with the serial number, the position of the hose in the circuit, and the characteristics of the original assembly.",
                    "HANSA-FLEX offers different types of hydraulic hoses and produces assembled hoses according to the real conditions of each application.",
                ]}
            />

            <MagazineTextBlock
                title="1. Record the machine and installation data"
                paragraphs={[
                    "Before removing the part, note down:",
                ]}
                bulletPoints={[
                    "manufacturer, model, year, and serial number of the machine;",
                    "hose function: pressure, return, suction, or pilot line;",
                    "point of origin and destination in the circuit;",
                    "total length and position of the fittings;",
                    "available space, movements, bends, and friction points.",
                ]}
                paragraphs2={[
                    "Photograph the installation from different angles. This helps preserve the original routing and prevents the new hose from being installed twisted, under tension, or with an inadequate bend radius.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem2}
                imageAlt="2. Read the markings on the existing hose"
                title="2. Read the markings on the existing hose"
                paragraphs={[
                    "The outer cover usually shows information such as manufacturer, type, nominal diameter, standard, and working pressure. Record the entire sequence, even if part of it seems unimportant.",
                    "When the hose has the X-CODE identification system, the process becomes simpler. The code gathers the assembly data and allows you to request an equivalent replacement more quickly and accurately.",
                    "Without reliable identification, the old sample can help, but it should not be the only reference. A previously installed hose may already have been specified incorrectly.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem3}
                imageAlt="3. Confirm the pressure and the inner diameter"
                title="3. Confirm the pressure and the inner diameter"
                paragraphs={[
                    "The hose must withstand the maximum working pressure of the system, including possible peaks. The fitting must also be compatible, since the capacity of the assembled hose is limited by the component with the lowest permissible pressure.",
                    "The inner diameter influences fluid velocity, pressure loss, and heating.",
                    "In a technical example from HANSA-FLEX, a flow rate of 40 liters per minute, at 200 bar, passes through a 13 mm hose at approximately 5 m/s. When the diameter is reduced to 10 mm, the velocity increases to about 8.5 m/s, an increase of 70%.",
                    "This increase can cause:",
                ]}
                bulletPoints={[
                    "greater heating;",
                    "noise in the system;",
                    "loss of efficiency;",
                    "accelerated wear;",
                    "premature component failures.",
                ]}
                paragraphs2={[
                    "More information is available in the article on calculating the diameter of hydraulic hoses and tubes.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem4}
                imageAlt="4. Check the fluid and the temperature"
                title="4. Check the fluid and the temperature"
                paragraphs={[
                    "Not every hose is compatible with every fluid. Mineral oil, biodegradable fluid, water-oil emulsions, glycols, and other media require specific inner materials.",
                    "The following must also be considered:",
                ]}
                bulletPoints={[
                    "fluid temperature;",
                    "ambient temperature;",
                    "proximity to engines, exhausts, or furnaces;",
                    "exposure to ozone and ultraviolet radiation;",
                    "contact with chemicals or abrasive materials.",
                ]}
                paragraphs2={[
                    "Using a hose outside the indicated range accelerates the aging of the elastomer and can cause hardening, cracking, and a significant reduction in service life.",
                ]}
            />

            <MagazineTextBlock
                title="5. Correctly identify the fittings"
                paragraphs={[
                    "Thread, diameter, seat angle, type of seal, orientation, and fitting material must match the machine's connection.",
                    "A visually similar connection may use a different standard and cause leaks, seat damage, or pressure loss. When in doubt, identification should be done with a caliper, a thread gauge, and verification of the sealing angle.",
                    "The hydraulic connection identification guide presents the main criteria for distinguishing standards such as NPT, BSP, JIC, and DIN.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem5}
                imageAlt="6. Prepare the information for the specification"
                title="6. Prepare the information for the specification"
                paragraphs={[
                    "To speed up technical service, gather:",
                ]}
                bulletPoints={[
                    "complete machine data;",
                    "photos of the installed hose and the connections;",
                    "all visible markings;",
                    "pressure, flow, fluid, and temperatures;",
                    "fitting measurements and length;",
                    "a sample of the removed hose, when possible.",
                ]}
                paragraphs2={[
                    "ISO/TS 17165-2:2018 reinforces that the selection must consider not only the hose, but also the routing, fabrication, installation, replacement, maintenance, and storage of the assembly.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem6}
                imageAlt="The right choice prevents the next breakdown"
                title="The right choice prevents the next breakdown"
                paragraphs={[
                    "Identifying a compatible hydraulic hose does not mean finding a part with only the same length and the same ends. It means selecting an assembly suited to the pressure, flow, fluid, temperature, fittings, and the real movements of the machine.",
                    "To reduce trial and error, rework, and the risk of another breakdown, contact the HANSA-FLEX technical team and request the specification of the assembled hose for your application.",
                ]}
                buttonName="Contact us"
                buttonLink="/contact-us"
            />

            <div style={{ backgroundColor: '#f8f8f8' }}>
                <br />
                <br />
                <br />
            </div>

        </div>
    )
}

export default CompatibleHydraulicHose
